/**
 * The local player: owns exactly one media generation, follows the room's
 * timeline, and reports what it actually observes.
 *
 * Two hard rules from plan/03 items 4 and 5 shape this file:
 *
 * 1. **One active generation.** Attaching new media detaches the old
 *    attachment, drops its listeners, and resets the report loop. Events that
 *    arrive for a superseded attachment are ignored by identity comparison,
 *    not by hoping they do not happen.
 * 2. **No commands.** Nothing in here sends a room command. Programmatic
 *    `play()`, `pause()`, `playbackRate`, and corrective seeks are local
 *    state application. User intent travels a separate path (the control bar
 *    and keyboard shortcuts in `src/app/`), which is why the `<video>` element
 *    is rendered *without* native controls: a native pause would be
 *    indistinguishable from state application and would be immediately undone
 *    by the correction tick.
 *
 * Correction runs on a fixed 250 ms timer, never on snapshot arrival, so a
 * burst of snapshots cannot turn into a burst of seeks. `PlaybackReport`
 * messages are untrusted hints about this connection only; a locally broken
 * player reports `joining` (not `buffering`), because a decode failure must
 * not hold a shared buffering barrier.
 */

import type {
  ActiveMedia,
  EntryId,
  MediaGeneration,
  PlaybackReportState,
  PlaybackState,
  SubtitleTrack,
} from '../generated';
import type { PlaybackReportMessage } from '../room/protocol';
import {
  attachActiveMedia,
  mediaErrorMessage,
  unsupportedTransportMessage,
  type MediaAttachment,
} from './adapter';
import { SYNC_TUNING, decideSync, expectedPositionMs } from './sync';

/** Local report state, i.e. `PlaybackReportState['state']`. */
export type ReportState = PlaybackReportState['state'];

/** What the last correction tick did, for the sync readout. */
export type CorrectionKind = 'none' | 'rate' | 'seek';

/** One measurement sample; see `PlaybackController.sample`. */
export interface SyncMeasurement {
  /** Client monotonic ms at capture. */
  readonly clientMs: number;
  /** Estimated server monotonic ms at the same instant, or `null`. */
  readonly serverMs: number | null;
  /** The element's `currentTime` in ms at the same instant. */
  readonly positionMs: number;
  /** The room's expected position at that server instant, or `null`. */
  readonly expectedMs: number | null;
  /** `expectedMs - positionMs`, or `null` when not measurable. */
  readonly driftMs: number | null;
  readonly rate: number;
  readonly state: ReportState;
  readonly roomState: PlaybackState['state'];
  readonly generation: MediaGeneration | null;
  readonly ready: boolean;
}

/** Re-send the current report at least this often, so presence stays fresh. */
const REPORT_HEARTBEAT_MS = 2500;

export interface PlaybackControllerHandlers {
  /** Estimated server clock, or `null` while no estimate exists. */
  serverNowMs: () => number | null;
  /** Sends one telemetry message (dropped while disconnected). */
  report: (message: PlaybackReportMessage) => void;
}

function activeMedia(playback: PlaybackState): ActiveMedia | null {
  switch (playback.state) {
    case 'playing':
    case 'paused':
    case 'buffering':
      return playback.media;
    case 'idle':
    case 'preparing':
    case 'failed':
      return null;
  }
}

export class PlaybackController {
  /** Element position in ms, sampled by the correction tick. */
  positionMs = $state(0);
  /**
   * The *room's* expected position in ms at the last tick, or `null` when
   * nothing is loaded or the clock is not estimated yet.
   *
   * This is the drift-corrected authoritative timeline (`expectedPositionMs`),
   * not this element's `currentTime`: anything that must agree across clients
   * — sponsorship-segment membership, for instance — has to use it, because a
   * local player legitimately lags or leads by a fraction of a second.
   */
  roomPositionMs = $state<number | null>(null);
  /** Duration in ms: the server's value, corrected by the element's metadata. */
  durationMs = $state(0);
  /** End of the buffered range covering the current position, in ms. */
  bufferedMs = $state(0);
  /** The browser refused programmatic playback; a gesture is required. */
  autoplayBlocked = $state(false);
  /** Element-level failure (decode, network, refused source). */
  localError = $state<string | null>(null);
  /** Non-null when this build cannot play the room's transport. */
  unsupported = $state<string | null>(null);
  /** Signed difference `expected - actual` in ms, or `null` when not measurable. */
  driftMs = $state<number | null>(null);
  /** Rate currently applied by drift correction. */
  appliedRate = $state(1);
  lastCorrection = $state<CorrectionKind>('none');
  /** Number of hard corrective seeks since the current media was attached. */
  correctiveSeeks = $state(0);
  /**
   * Learned resume latency of a hard seek, in ms: how far behind the room the
   * element lands when it restarts after `currentTime` is assigned. Corrective
   * seeks aim this far ahead so they land on the room position.
   */
  seekLeadMs = $state(0);
  reportState = $state<ReportState>('joining');
  muted = $state(false);
  volume = $state(1);
  subtitles = $state<readonly SubtitleTrack[]>([]);
  /** Index into `subtitles`, or `null` for off. Local preference only. */
  activeSubtitle = $state<number | null>(null);

  readonly #handlers: PlaybackControllerHandlers;

  #video: HTMLVideoElement | null = null;
  #attachment: MediaAttachment | null = null;
  #removeListeners: (() => void) | null = null;
  #playback: PlaybackState = { state: 'idle' };
  #entry: EntryId | null = null;
  #generation: MediaGeneration | null = null;
  #playbackUrl: string | null = null;

  #tickTimer: number | undefined;
  /** Bypass deadband/cooldown on the next tick (attach, entry change, resync). */
  #forceAlign = true;
  #lastSeekAtMs: number | null = null;
  /** Client time at which the last seek's residual drift can be measured. */
  #seekObservationDueMs: number | null = null;
  #everReady = false;
  #stalled = false;
  #ended = false;
  #playRequested = false;
  /** Set false while the room socket is down; correction and play are held. */
  #connected = true;
  #lastReport: ReportState | null = null;
  #lastReportAtMs = 0;
  #lastReportGeneration: MediaGeneration | null = null;

  constructor(handlers: PlaybackControllerHandlers) {
    this.#handlers = handlers;
  }

  /** Starts the correction/report tick. */
  start(): void {
    if (this.#tickTimer !== undefined) return;
    this.#tickTimer = setInterval(() => this.#tick(), SYNC_TUNING.tickIntervalMs);
  }

  stop(): void {
    clearInterval(this.#tickTimer);
    this.#tickTimer = undefined;
  }

  /** Releases media, listeners, and timers. The instance is not reusable. */
  dispose(): void {
    this.stop();
    this.#detachMedia();
    this.#video = null;
  }

  setVideoElement(video: HTMLVideoElement | null): void {
    if (this.#video === video) return;
    this.#detachMedia();
    this.#video = video;
    if (video !== null) {
      this.muted = video.muted;
      this.volume = video.volume;
      // A freshly mounted element has nothing attached; the next
      // `applyPlayback` (or the pending one) attaches the current media.
      this.#applyMedia(this.#playback);
    }
  }

  /**
   * Applies a room `PlaybackState`. Pure state application: attaches/detaches
   * media and records intent. It never touches `currentTime` directly — the
   * tick does that once the asset is actually seekable, so the position is not
   * captured before the download progressed (plan/03 item 7).
   */
  applyPlayback(playback: PlaybackState): void {
    this.#playback = playback;
    this.#applyMedia(playback);
  }

  /**
   * Called after a reconnect: the next tick realigns hard instead of nudging,
   * because the room may have moved arbitrarily while we were away.
   */
  requestResync(): void {
    this.#forceAlign = true;
  }

  /**
   * Connection state. While the socket is down the room's timeline is unknown,
   * so local playback pauses and correction stops (plan/03 item 11); the
   * attached asset and its buffer are kept so reconnecting is cheap. Coming
   * back re-aligns hard against the fresh snapshot.
   */
  setConnected(connected: boolean): void {
    if (this.#connected === connected) return;
    this.#connected = connected;
    if (connected) {
      this.#forceAlign = true;
      // The new connection has never heard from this player.
      this.#resetReports();
      return;
    }
    this.#video?.pause();
    if (this.#video !== null) this.#video.playbackRate = 1;
    this.appliedRate = 1;
    this.driftMs = null;
    this.lastCorrection = 'none';
  }

  /**
   * User gesture that starts local playback after an autoplay refusal. It
   * aligns to the room's *current* expected position and sends no command, so
   * a late joiner cannot rewind or unpause the room.
   */
  joinPlayback(): void {
    const video = this.#video;
    if (video === null || this.#attachment === null) return;
    const expected = this.#expectedMs();
    if (expected !== null && this.#seekable(video)) {
      video.currentTime = expected / 1000;
      this.#lastSeekAtMs = performance.now();
    }
    this.autoplayBlocked = false;
    this.#forceAlign = true;
    // Must be called synchronously inside the gesture to count as one.
    this.#requestPlay(video);
  }

  /** Local caption preference; re-applied after every reattach. */
  selectSubtitle(index: number | null): void {
    this.activeSubtitle = index;
    this.#attachment?.selectSubtitle(index);
  }

  setMuted(muted: boolean): void {
    if (this.#video === null) return;
    this.#video.muted = muted;
  }

  setVolume(volume: number): void {
    if (this.#video === null) return;
    const clamped = Math.min(1, Math.max(0, volume));
    this.#video.volume = clamped;
    if (clamped > 0 && this.#video.muted) this.#video.muted = false;
  }

  /** Local-only fullscreen toggle. */
  async toggleFullscreen(): Promise<void> {
    const video = this.#video;
    if (video === null) return;
    try {
      if (document.fullscreenElement === null) await video.requestFullscreen();
      else await document.exitFullscreen();
    } catch {
      // Fullscreen refusal is not an application error.
    }
  }

  /**
   * Instrumentation sample for synchronization measurement (see
   * docs/synchronization.md). Read-only; no analytics, no network.
   *
   * Position, client clock, and estimated server clock are captured in one
   * step, reading `currentTime` directly rather than the tick-sampled value:
   * comparing two clients requires positions that belong to a known server
   * instant, not values up to one tick stale.
   */
  sample(): SyncMeasurement {
    const clientMs = performance.now();
    const video = this.#video;
    const positionMs = video === null ? 0 : video.currentTime * 1000;
    const serverMs = this.#handlers.serverNowMs();
    const expected = expectedPositionMs(this.#playback, serverMs);
    return {
      clientMs,
      serverMs,
      positionMs,
      expectedMs: expected,
      driftMs: expected === null ? null : expected - positionMs,
      rate: video === null ? 1 : video.playbackRate,
      state: this.reportState,
      roomState: this.#playback.state,
      generation: this.#generation,
      ready: this.#everReady,
    };
  }

  #expectedMs(): number | null {
    return expectedPositionMs(this.#playback, this.#handlers.serverNowMs());
  }

  #seekable(video: HTMLVideoElement): boolean {
    return video.readyState >= HTMLMediaElement.HAVE_METADATA && video.seekable.length > 0;
  }

  #applyMedia(playback: PlaybackState): void {
    const video = this.#video;
    const media = activeMedia(playback);
    const entry: EntryId | null = playback.state === 'idle' ? null : playback.entry;

    if (media === null) {
      if (this.#attachment !== null) this.#detachMedia();
      this.unsupported = null;
      this.#entry = entry;
      return;
    }

    const unsupported = unsupportedTransportMessage(media.transport);
    if (unsupported !== null) {
      if (this.#attachment !== null) this.#detachMedia();
      this.unsupported = unsupported;
      this.#entry = entry;
      return;
    }
    this.unsupported = null;

    const sameAsset =
      this.#attachment !== null &&
      this.#generation === media.generation &&
      this.#playbackUrl === media.playback_url;

    if (!sameAsset) {
      this.#detachMedia();
      if (video === null) return;
      const attachment = attachActiveMedia(video, media);
      if (attachment === null) {
        this.unsupported = unsupportedTransportMessage(media.transport) ?? 'Unsupported media.';
        return;
      }
      this.#attachment = attachment;
      this.#generation = media.generation;
      this.#playbackUrl = media.playback_url;
      this.durationMs = media.duration_ms;
      this.subtitles = attachment.subtitles;
      this.correctiveSeeks = 0;
      this.#everReady = false;
      this.#stalled = false;
      this.#ended = false;
      this.#playRequested = false;
      this.#lastSeekAtMs = null;
      this.#forceAlign = true;
      this.localError = null;
      this.driftMs = null;
      this.appliedRate = 1;
      video.playbackRate = 1;
      // A caption choice survives an asset swap; an out-of-range index does not.
      const keep =
        this.activeSubtitle !== null && this.activeSubtitle < attachment.subtitles.length
          ? this.activeSubtitle
          : null;
      this.activeSubtitle = keep;
      attachment.selectSubtitle(keep);
      this.#bindListeners(video, attachment);
      this.#resetReports();
    } else if (this.#entry !== entry) {
      // Same bytes (a duplicate queue entry can share one asset), new item:
      // realign hard rather than drifting from the previous item's position.
      this.#forceAlign = true;
      this.#ended = false;
      this.durationMs = media.duration_ms;
    }

    this.#entry = entry;
  }

  #detachMedia(): void {
    this.#removeListeners?.();
    this.#removeListeners = null;
    this.#attachment?.detach();
    this.#attachment = null;
    this.#generation = null;
    this.#playbackUrl = null;
    this.subtitles = [];
    this.positionMs = 0;
    this.roomPositionMs = null;
    this.bufferedMs = 0;
    this.driftMs = null;
    this.appliedRate = 1;
    this.lastCorrection = 'none';
    this.autoplayBlocked = false;
    this.#everReady = false;
    this.#stalled = false;
    this.#ended = false;
    this.#playRequested = false;
    this.#seekObservationDueMs = null;
    if (this.#video !== null) this.#video.playbackRate = 1;
    this.#resetReports();
  }

  #resetReports(): void {
    this.#lastReport = null;
    this.#lastReportAtMs = 0;
    this.#lastReportGeneration = null;
    this.reportState = 'joining';
  }

  #bindListeners(video: HTMLVideoElement, attachment: MediaAttachment): void {
    /** Every handler drops events belonging to a superseded attachment. */
    const current = (): boolean => this.#attachment === attachment;

    const onLoadedMetadata = (): void => {
      if (!current()) return;
      if (Number.isFinite(video.duration) && video.duration > 0) {
        this.durationMs = Math.round(video.duration * 1000);
      }
      this.#forceAlign = true;
    };
    const onCanPlay = (): void => {
      if (!current()) return;
      this.#everReady = true;
      this.#stalled = false;
    };
    const onWaiting = (): void => {
      if (!current()) return;
      // A user pause and an ordinary seek are not network stalls; only report
      // buffering when the room wants to be playing and we cannot.
      if (this.#playback.state === 'playing' && !video.seeking) this.#stalled = true;
    };
    const onStalled = onWaiting;
    const onPlaying = (): void => {
      if (!current()) return;
      this.#stalled = false;
      this.#ended = false;
      this.autoplayBlocked = false;
      this.localError = null;
    };
    const onEnded = (): void => {
      if (!current()) return;
      this.#ended = true;
      this.#stalled = false;
    };
    const onError = (): void => {
      if (!current()) return;
      this.localError = mediaErrorMessage(video.error);
    };
    const onVolumeChange = (): void => {
      if (!current()) return;
      this.muted = video.muted;
      this.volume = video.volume;
    };
    const onProgress = (): void => {
      if (!current()) return;
      this.bufferedMs = bufferedEndMs(video);
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('stalled', onStalled);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);
    video.addEventListener('volumechange', onVolumeChange);
    video.addEventListener('progress', onProgress);

    this.#removeListeners = () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('stalled', onStalled);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
      video.removeEventListener('volumechange', onVolumeChange);
      video.removeEventListener('progress', onProgress);
    };
  }

  #requestPlay(video: HTMLVideoElement): void {
    if (this.#playRequested) return;
    this.#playRequested = true;
    const attachment = this.#attachment;
    void video
      .play()
      .then(() => {
        this.#playRequested = false;
        if (this.#attachment !== attachment) return;
        this.autoplayBlocked = false;
      })
      .catch((error: unknown) => {
        this.#playRequested = false;
        if (this.#attachment !== attachment) return;
        const name = error instanceof DOMException ? error.name : '';
        // `AbortError` means a newer load/pause superseded this request.
        if (name === 'AbortError') return;
        if (name === 'NotAllowedError') {
          this.autoplayBlocked = true;
          return;
        }
        this.localError =
          video.error === null
            ? 'The browser refused to start playback of the prepared file.'
            : mediaErrorMessage(video.error);
      });
  }

  #tick(): void {
    const video = this.#video;
    const attachment = this.#attachment;
    // The room's timeline exists independently of this element, so the
    // authoritative position is refreshed even with nothing attached (and
    // becomes `null` on its own while the clock is unknown).
    this.roomPositionMs = this.#expectedMs();
    if (video === null || attachment === null) {
      this.#publishReport();
      return;
    }

    this.positionMs = Math.round(video.currentTime * 1000);
    this.bufferedMs = bufferedEndMs(video);

    if (!this.#connected) {
      // No authoritative timeline: hold position and correct nothing. The
      // readout still tracks what we *would* report; nothing is sent, because
      // the socket that would carry it is gone.
      if (!video.paused) video.pause();
      this.reportState = this.#deriveReport();
      return;
    }

    const wantsPlay = this.#playback.state === 'playing' && !this.#ended;
    if (wantsPlay) {
      if (video.paused && !this.autoplayBlocked && this.localError === null) {
        this.#requestPlay(video);
      }
    } else if (!video.paused) {
      video.pause();
    }

    const expected = this.#expectedMs();
    if (expected === null || !this.#seekable(video)) {
      this.driftMs = null;
      this.#publishReport();
      return;
    }

    // While the element is seeking its `currentTime` is the target, not an
    // observation; measuring would produce a phantom error of zero. A long
    // cold seek must not leave the last measured value on screen either.
    if (video.seeking) {
      this.driftMs = null;
      this.#publishReport();
      return;
    }

    const actual = video.currentTime * 1000;
    const nowMs = performance.now();
    this.driftMs = expected - actual;
    this.#observeSeekLatency(nowMs, wantsPlay);

    // Paused/buffering/blocked players hold the fixed anchor: align, but never
    // nudge the rate of something that is not playing.
    const action = decideSync({
      expectedMs: expected,
      actualMs: actual,
      currentRate: video.playbackRate,
      force: this.#forceAlign,
      nowMs,
      lastSeekMs: this.#lastSeekAtMs,
    });
    this.#forceAlign = false;

    switch (action.kind) {
      case 'seek': {
        // Seek ahead by the measured resume latency when the room is playing,
        // so playback restarts *at* the room position instead of behind it.
        const lead = wantsPlay ? this.seekLeadMs : 0;
        const target = Math.min(action.positionMs + lead, this.durationMs);
        video.currentTime = target / 1000;
        video.playbackRate = 1;
        this.appliedRate = 1;
        this.#lastSeekAtMs = nowMs;
        this.#seekObservationDueMs = nowMs + SYNC_TUNING.seekObservationDelayMs;
        this.lastCorrection = 'seek';
        this.correctiveSeeks += 1;
        break;
      }
      case 'rate': {
        // Rate corrections only make sense while actually playing.
        const rate = wantsPlay && !video.paused ? action.rate : 1;
        video.playbackRate = rate;
        this.appliedRate = rate;
        this.lastCorrection = rate === 1 ? 'none' : 'rate';
        break;
      }
      case 'hold':
        if (!wantsPlay && video.playbackRate !== 1) {
          video.playbackRate = 1;
          this.appliedRate = 1;
          this.lastCorrection = 'none';
        }
        break;
    }

    this.#publishReport();
  }

  /**
   * Refines `seekLeadMs` from the drift left over once a seek has actually
   * resumed. Runs once per seek, only while playing, and only from a real
   * observation — never from an assumption about decoder behaviour.
   */
  #observeSeekLatency(nowMs: number, wantsPlay: boolean): void {
    const due = this.#seekObservationDueMs;
    if (due === null || nowMs < due) return;
    this.#seekObservationDueMs = null;
    const drift = this.driftMs;
    if (!wantsPlay || drift === null) return;
    const blended =
      this.seekLeadMs + SYNC_TUNING.seekLeadSmoothing * drift;
    this.seekLeadMs = Math.min(Math.max(blended, 0), SYNC_TUNING.maxSeekLeadMs);
  }

  /**
   * True when the room's expected position is actually buffered here.
   *
   * Readiness has to mean "I can play *the room's* position", not "I have
   * data somewhere": a throttled viewer sitting 20 s behind has plenty of
   * buffered data around its own frozen position, and reporting `ready` from
   * that would release a shared buffering barrier it cannot honour.
   */
  #readyAtRoomPosition(video: HTMLVideoElement): boolean {
    const expected = this.#expectedMs();
    if (expected === null) return true;
    const seconds = expected / 1000;
    for (let index = 0; index < video.buffered.length; index += 1) {
      if (video.buffered.start(index) <= seconds && video.buffered.end(index) >= seconds) {
        return true;
      }
    }
    // Right at the end of the asset there is nothing left to buffer.
    return expected >= this.durationMs - 250;
  }

  #deriveReport(): ReportState {
    const video = this.#video;
    if (video === null || this.#attachment === null) return 'joining';
    if (this.autoplayBlocked) return 'autoplay-blocked';
    // A locally broken player is not ready and not buffering: reporting
    // `buffering` would hold a shared barrier for a failure recovery cannot fix.
    if (this.localError !== null) return 'joining';
    if (this.#ended) return 'ended';
    if (!this.#everReady) return 'joining';
    if (
      this.#stalled ||
      video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
      !this.#readyAtRoomPosition(video)
    ) {
      return 'buffering';
    }
    return video.paused || video.seeking ? 'ready' : 'playing';
  }

  #publishReport(): void {
    const state = this.#deriveReport();
    this.reportState = state;
    // Generation 0 means "presence not tied to any media": the server accepts
    // it for plain `joining`/`autoplay-blocked` presence while the room is
    // idle/preparing, and ignores any report whose generation is neither 0 nor
    // the currently active one.
    const generation = this.#generation ?? 0;

    const nowMs = performance.now();
    const changed = state !== this.#lastReport || generation !== this.#lastReportGeneration;
    if (!changed && nowMs - this.#lastReportAtMs < REPORT_HEARTBEAT_MS) return;

    this.#lastReport = state;
    this.#lastReportGeneration = generation;
    this.#lastReportAtMs = nowMs;
    this.#handlers.report({ type: 'playback-report', generation, report: { state } });
  }
}

/** End of the buffered range containing the current position, in ms. */
function bufferedEndMs(video: HTMLVideoElement): number {
  const time = video.currentTime;
  for (let index = 0; index < video.buffered.length; index += 1) {
    if (video.buffered.start(index) <= time + 0.25 && video.buffered.end(index) >= time) {
      return Math.round(video.buffered.end(index) * 1000);
    }
  }
  return Math.round(time * 1000);
}
